import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Modal,
  Typography,
  Tooltip,
  Select,
  Stack,
  TextField,
  FormHelperText,
  FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import makeStyles from "@material-ui/styles/makeStyles";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { any, boolean, object, string, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { useDispatch } from "react-redux";

import FormInput from '../form/FormInput';
import AntSwitch from "./AntSwitch";
import {ReactComponent as CalendarIcon} from '../../assets/svg/calendar.svg';
import IMG_PERK_PLACEHOLDER from "../../assets/img/perk-placeholder.jpg";
import { createPerk, updatePerk } from "../../redux/actions/perk";
import { resizePerkFeaturedImage } from '../../utils/resizeFile';
import config from "../../config/config";

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const REQUIREMENT_TYPES = [
  "Previously booked",
  "Previously booked 2 times",
  "Previously booked 3 times",
  "Min 2 hour booking",
  "Min 4 hour booking",
  "Min 6 hour booking",
  "Min 8 hour booking",
  "Overnight bookings only",
  "FMTY bookings only",
  "Available to all",
  "Other (check description)",
];


export default function CreatePerk({
  visible,
  onClose,
  perk,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const useStyles = makeStyles({
    menuItem: {
      color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
      fontSize: '13px',
      padding: '10px 16px',
      "&:hover": {
        color: '#009ef7',
        backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
      },
    },
    menuItemSelected: {
      color: '#fff',
      backgroundColor: '#009ef7 !important',
      alignItems: 'center',
      justifyContent: 'space-between',
      "&:hover": {
        color: '#fff',
        backgroundColor: '#009ef7 !important',
      },
    },
  });
  const styles = useStyles();

  const defaultValues = {
    title: '',
    summary: '',
    location: '',
    requirement: '',
    description: '',
    status: true,
  };

  const createTicketSchema = object({
    title: string().min(1, 'Title is required'),
    summary: any(),
    location: string().min(1, 'Location is required'),
    requirement: string().min(1, 'Requirement is required'),
    description: any(),
    featuredImage: perk?._id ? any() : z
      .any()
      .refine((files) => files?.length === 1, "Image is required.")
      .refine((files) => files?.[0]?.size <= MAX_FILE_SIZE, `Max file size is 5MB.`)
      .refine(
        (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
        ".jpg, .jpeg, .png and .webp files are accepted."
      ),
    status: boolean(),
  });
  const methods = useForm({
    resolver: zodResolver(createTicketSchema),
    defaultValues
  });
  const { register, formState: { errors } } = methods;

  const watchPerkStatus = methods.watch('status', true);
  const watchRequirement = methods.watch('requirement', '');
  const watchFeaturedImage = methods.watch('featuredImage', []);

  useEffect(() => {
    handleSelectFeaturedImage(watchFeaturedImage);
  }, [watchFeaturedImage]);

  useEffect(() => {
    methods.setValue("title", perk?.title);
    methods.setValue("summary", perk?.summary);
    methods.setValue("location", perk?.location);
    if (perk?.expiryDate) {
      setValue(perk?.expiryDate);
    }
    methods.setValue("requirement", perk?.requirement);
    methods.setValue("description", perk?.description);
    setSelectedFeatureImage(perk?.featuredImage);
    methods.setValue("status", perk?.status === 0 ? true : false);
  }, [perk]);

  const [value, setValue] = useState(moment().add(30, 'days'));
  const [openDateTimePicker, setOpenDateTimePicker] = useState(false);
  const [featuredImage, setFeaturedImage] = useState();
  const [selectedFeatureImage, setSelectedFeatureImage] = useState();

  const onChangeRequirement = (e) => {
    methods.setValue("requirement", e.target.value);
  }

  const handleFeaturedImageUpload = () => {
    document.querySelector('#FeaturedImage-Selection').value = null;
    document.querySelector('#FeaturedImage-Selection').click();
  }
  const handleFeaturedImageRemove = () => {
    setSelectedFeatureImage(null);
    setFeaturedImage(null);
  }

  const handleSelectFeaturedImage = async files => {
    if (files.length === 0) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type)) {
      return;
    }
    if (files[0].size > MAX_FILE_SIZE) {
      return;
    }
    const file = files[0];
    const resizedFile = await resizePerkFeaturedImage(file, file.type.split('/').pop().toUpperCase());
    setFeaturedImage(resizedFile);
    const reader = new FileReader();
    reader.readAsDataURL(resizedFile);
    reader.onloadend = (e) => {
      setSelectedFeatureImage(reader.result);
    };
  }

  const handlePerkStatus = event => {
    methods.setValue("status", event.target.checked);
  }

  // ? Form Handler
  const onSubmitHandler = async (data) => {
    let formData = new FormData();
    if (perk?._id) {
      formData.append("id", perk?._id);
    }
    formData.append("title", data.title);
    formData.append("summary", data.summary);
    formData.append("location", data.location);
    formData.append("expiryDate", value);
    formData.append("requirement", data.requirement);
    formData.append("description", data.description);
    formData.append("featuredImage", featuredImage);
    formData.append("status", data.status);
    let res;
    if (perk?._id) {
      res = await dispatch(updatePerk(formData));
    } else {
      res = await dispatch(createPerk(formData));
    }
    if (res != null) {
      methods.reset(defaultValues);
      handleFeaturedImageRemove();
      onClose();
    }
  };

  return (
    <Modal
      open={visible}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: '650px'},
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          maxHeight: '95%',
          overflowY: 'auto',
          '::-webkit-scrollbar': {
            width: '12px',
          },
          '::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            borderRadius: '6px',
            border: '3px solid transparent',
            backgroundClip: 'content-box',
            backgroundColor: '#c1c1c1',
          }
        }}
      >
        <Grid sx={{ margin: 'auto', p: 4 }} className="text-center">
          <CloseIcon
            onClick={onClose}
            sx={{
              color: theme.palette.mode === 'light' ? '#181c32' : '#fff',
              position: 'absolute',
              right: '16px',
              top: '16px', 
              display: { md: 'none' }
            }}
          />
          <Typography id="modal-modal-title" sx={{ fontSize: '24px', fontWeight: 700, mt: 1.5, textAlign: 'center' }}>
            Enter Perk Details
          </Typography>
          <FormProvider {...methods}>
            <Box
              display='flex'
              flexDirection='column'
              component='form'
              noValidate
              autoComplete='off'
              sx={{ mt: 2.5 }}
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <Grid>
                <Stack direction="row" alignItems="center">
                  <Typography sx={{ fontSize: '13px', fontWeight: '600', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Perk Title <span style={{ color: 'red' }}>*</span></Typography>
                  <Tooltip title={'Your title should be short \nand catchy.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Stack>
                <FormInput
                  placeholder='Enter Perk Title'
                  type='text'
                  name='title'
                  fullWidth
                  sx={{ mt: 1 }}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center">
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, textAlign: 'left' }}>Summary</Typography>
                  <Tooltip title={'This is a short excerpt\n of the Perk at a glance.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Stack>
                <FormInput
                  placeholder='A short blurb about this perk'
                  type='text'
                  name='summary'
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 1 }}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={6}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: '600', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Location <span style={{ color: 'red' }}>*</span></Typography>
                    <Tooltip title={'Enter the location that this\nPerk is valid for.'} placement="top" enterTouchDelay={0}>
                      <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                    </Tooltip>
                  </Stack>
                  <FormInput
                    placeholder='Enter city or country'
                    type='text'
                    name='location'
                    fullWidth
                    sx={{ mt: 1 }}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: '600', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Expiry Date <span style={{ color: 'red' }}>*</span></Typography>
                    <Tooltip title={'Enter the date when the Perk expires\nand will no longer display.'} placement="top" enterTouchDelay={0}>
                      <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                    </Tooltip>
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        open={openDateTimePicker}
                        onOpen={() => setOpenDateTimePicker(true)}
                        onClose={() => setOpenDateTimePicker(false)}
                        inputFormat="YYYY-MM-DD"
                        renderInput={(props) => {
                          props.InputProps = {
                            ...props.InputProps,
                            disableUnderline: true,
                            style: {
                              color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
                              fontSize: '0.9rem',
                              fontWeight: 400,
                            }
                          };
                          return (
                            <TextField
                              fullWidth
                              variant="standard"
                              {...props}
                              sx={{ svg: { color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674' } }}
                              onClick={() => setOpenDateTimePicker(true)}
                            />
                          );
                        }}
                        value={value}
                        onChange={(newValue) => setValue(newValue)}
                        InputAdornmentProps={{ position: 'start' }}
                        components={{ OpenPickerIcon: CalendarIcon }}
                        minDate={moment().toDate()}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              </Grid>
              <FormControl sx={{ mt: 2 }} error={!!errors['requirement']}>
                <Stack direction="row" alignItems="center">
                  <Typography sx={{ fontSize: '14px', fontWeight: '600', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Requirement <span style={{ color: 'red' }}>*</span></Typography>
                  <Tooltip title={'Specify the conditions to be\neligible for this Perk.'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Stack>
                <Select
                  fullWidth
                  inputProps={{ sx: { color: '#5e6278', fontWeight: 400 } }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: '250px' } } }}
                  displayEmpty={true}
                  value={watchRequirement}
                  renderValue={value => value ? value : 'Select condition'}
                  sx={{
                    mt: 1,
                    textAlign: 'left',
                    padding: 0,
                    '.MuiSelect-select': { padding: '12px 20px' },
                    '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
                  }}
                  onChange={onChangeRequirement}
                >
                  {REQUIREMENT_TYPES.map((item, index) => (
                    <MenuItem
                      key={item}
                      value={item}
                      selected={item === watchRequirement}
                      classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}
                    >
                      {item}
                      {item === watchRequirement && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                    </MenuItem>
                  ))}
                </Select>
                {errors['requirement'] && <FormHelperText>{errors['requirement'].message}</FormHelperText>}
              </FormControl>
              <Grid sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center">
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, textAlign: 'left' }}>Description (500 characters max)</Typography>
                </Stack>
                <FormInput
                  placeholder='A detailed description about the Perk and how to be eligible.'
                  type='text'
                  name='description'
                  fullWidth
                  multiline
                  rows={5}
                  sx={{ mt: 1 }}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              <Stack direction="row" alignItems="flex-start" sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ mt: 2, width: '200px' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, textAlign: 'left' }}>Featured Image <span style={{ color: 'red' }}>*</span></Typography>
                  <Tooltip title={'Upload an attention grabbing\nimage for this Perk!'} placement="top" enterTouchDelay={0}>
                    <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Stack>
                <Grid>
                  <div className="IDCard-warpper">
                    <Box
                      component="img"
                      src={selectedFeatureImage
                        ? selectedFeatureImage.includes('upload')
                          ? `${config.SERVER_BASE_URL}/${selectedFeatureImage}`
                          : selectedFeatureImage
                        : IMG_PERK_PLACEHOLDER}
                      style={{
                        width: '150px',
                        borderRadius: '6px',
                        boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075)',
                        border: '3px solid #fff'
                      }}
                      alt="IMG_FEATURED_IMAGE"
                    />
                    <Tooltip title="Upload Photo" placement="top" enterTouchDelay={0}>
                      <span className="IDCard-upload" onClick={handleFeaturedImageUpload}>
                        <i className="fa fa-pen"></i>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove Upload" placement="top" enterTouchDelay={0}>
                      <span className="IDCard-remove " onClick={handleFeaturedImageRemove}>
                        <i className="fa fa-remove"></i>
                      </span>
                    </Tooltip>
                  </div>
                  {errors['featuredImage'] && (
                    <p style={{ color: '#d32f2f', fontWeight: 400, fontSize: '0.75rem', marginInlineStart: '14px' }}>
                      {errors['featuredImage']?.message}
                    </p>
                  )}
                  <Typography sx={{ fontSize: '12px', textAlign: 'left', color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674' }}>Recommended size 720x420px</Typography>
                  <input type="file" name="featuredImage" id="FeaturedImage-Selection" style={{ visibility: 'hidden' }} {...register('featuredImage')} />
                </Grid>
              </Stack>
              <Stack direction="row" alignItems="flex-start" sx={{ mt: 0 }}>
                <Stack direction="row" alignItems="center" sx={{ width: '200px' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, textAlign: 'left' }}>Status <span style={{ color: 'red' }}>*</span></Typography>
                  <Tooltip title={'Set this Perk as Active\nor Inactive.'} placement="top">
                    <ErrorIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674', width: '20px', height: '14px' }} /> 
                  </Tooltip>
                </Stack>
                <Grid>
                  <AntSwitch
                    checked={watchPerkStatus}
                    onChange={handlePerkStatus}
                    inputProps={{ 'aria-label': 'ant design' }}
                  />
                </Grid>
              </Stack>
              <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ padding: '11px 35px', fontSize: '14px', lineHeight: '21px', fontWeight: 600, textTransform: 'none', color: '#fff' }}
                >
                  Save
                </Button>
                <Button
                  onClick={onClose}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                    padding: '11px 30px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#6d6d80',
                    marginInlineStart: 1,
                    '&:hover ': {
                      backgroundColor: theme.palette.mode === 'light' ? '#eff2f5' : '#323248',
                    }
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Box>
          </FormProvider>
        </Grid>
      </Box>
    </Modal>
  );
}
